import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPricingPlans, getSubscription, createCheckoutSession, changePlan, previewChangePlan, detectBillingCurrency, applePurchaseSync } from '../lib/api';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { getUser } from '../lib/auth';
import { detectCurrency, CURRENCY_SYMBOLS } from '../lib/currency';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import { Check, Star, ArrowLeft, Home, Users, Zap, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { isIOS } from '../lib/socialAuth';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { BACKEND_URL } from '../lib/config';
import { configureRevenueCat, getIOSPackages, getIOSDiagnostics, buyIOSPackage, restoreIOSPurchases, findPackage, priceStringForPlan } from '../lib/revenuecat';

export default function PricingPage() {
  const [plans, setPlans] = useState({});
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('month');
  const [discountPct, setDiscountPct] = useState(20);
  const [currency, setCurrency] = useState(detectCurrency());
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [iosPackages, setIosPackages] = useState([]);
  const [iosDiag, setIosDiag] = useState(null);
  const [iosError, setIosError] = useState(null);
  const [confirmChange, setConfirmChange] = useState(null);
  const [currentBillingCycle, setCurrentBillingCycle] = useState('month');
  const navigate = useNavigate();
  const user = getUser();
  const sym = CURRENCY_SYMBOLS[currency] || '£';

  // A plan counts as "current" (locked) ONLY when there is a real PAID subscription
  // AND it matches BOTH the plan tier and the selected billing cycle. This lets a
  // monthly subscriber switch to the YEARLY version of any plan (and vice-versa).
  const PAID_STATUSES = ['active', 'past_due', 'canceling'];
  const hasPaidSubscription = PAID_STATUSES.includes(subscriptionStatus);
  const isPlanCurrent = (planKey) =>
    hasPaidSubscription && planKey === currentPlan && currentBillingCycle === billingCycle;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const subRes = await getSubscription();
      const locked = !!subRes.data.currency_locked;
      let cur = locked ? (subRes.data.currency || 'gbp') : detectCurrency();
      // Refine with server-side geo-IP when it actually detected a country (production/CDN)
      if (!locked) {
        try {
          const d = await detectBillingCurrency();
          if (d.data?.country && d.data?.currency) cur = d.data.currency;
        } catch (e) { /* keep local detection */ }
      }
      const plansRes = await getPricingPlans(cur);
      setPlans(plansRes.data.plans);
      setDiscountPct(plansRes.data.yearly_discount_percent ?? 20);
      setCurrentPlan(subRes.data.current_plan);
      setCurrency(cur);
      setHasSubscription(!!subRes.data.stripe_subscription_id);
      setSubscriptionStatus(subRes.data.subscription_status || null);
      setCurrentBillingCycle(subRes.data.billing_cycle === 'year' ? 'year' : 'month');
    } catch (error) {
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
    // iOS: load Apple/RevenueCat packages WITHOUT blocking the page (never gate loading on this)
    if (isIOS() && user?.companyId) {
      loadIosPackages(user.companyId);
    }
  };

  const loadIosPackages = async (companyId) => {
    try {
      await configureRevenueCat(companyId);
      const pkgs = await getIOSPackages();
      setIosPackages(pkgs);
      if (!pkgs || pkgs.length === 0) {
        setIosDiag(await getIOSDiagnostics());
      } else {
        setIosDiag(null);
      }
    } catch (e) {
      setIosDiag({ error: String(e?.message || e) });
    }
  };

  // iOS: buy a plan via Apple In-App Purchase (RevenueCat)
  const handleBuyIOS = async (planKey) => {
    if (isPlanCurrent(planKey)) return;
    if (user?.role !== 'admin') {
      toast.error('Only admins can change the subscription plan');
      return;
    }
    const pkg = findPackage(iosPackages, planKey, 'month');
    if (!pkg) {
      if (!iosPackages || iosPackages.length === 0) {
        toast.error('Subscriptions are still being set up on the App Store. Please try again in a few minutes.');
      } else {
        toast.error('This plan is not available yet. Please try again shortly.');
      }
      return;
    }
    setProcessingPlan(planKey);
    setIosError(null);
    try {
      try {
        await buyIOSPackage(pkg);
      } catch (pErr) {
        const msg = String(pErr?.message || pErr || '');
        const code = String(pErr?.code ?? pErr?.errorCode ?? '');
        const cancelled = pErr?.userCancelled === true || code === '1' || /cancel/i.test(msg);
        if (cancelled) return;
        setIosError(`PURCHASE [code ${code || '?'}]: ${msg}`);
        toast.error('Purchase could not be completed. Please try again.');
        return;
      }
      // Purchase succeeded on Apple + RevenueCat. Now sync our backend.
      try {
        await applePurchaseSync();
      } catch (sErr) {
        const detail = sErr?.response?.data?.detail || sErr?.message || String(sErr);
        setIosError(`SYNC: ${detail}`);
        // Payment went through; the RevenueCat webhook will reconcile server-side.
        toast.success('Payment received — activating your subscription…');
        await loadData();
        return;
      }
      toast.success('Subscription active — welcome aboard!');
      await loadData();
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleRestoreIOS = async () => {
    try {
      await restoreIOSPurchases();
      await applePurchaseSync();
      toast.success('Purchases restored.');
      await loadData();
    } catch (e) {
      toast.error('Could not restore purchases.');
    }
  };

  const handleSelectPlan = async (planKey) => {
    if (isIOS()) return; // iOS: plans are informational only (no in-app purchase)
    if (isPlanCurrent(planKey)) return;
    
    if (user?.role !== 'admin') {
      toast.error('Only admins can change the subscription plan');
      return;
    }

    setProcessingPlan(planKey);
    try {
      if (hasPaidSubscription) {
        // Existing paid subscription: preview the exact effect (prorated charge,
        // new price, next renewal) and ask the customer to confirm BEFORE applying.
        const prev = await previewChangePlan(planKey, billingCycle);
        setConfirmChange({ ...prev.data, planKey });
        setProcessingPlan(null);
      } else {
        // Web & Android (iOS returned early above and uses Apple IAP).
        // On native Android, Stripe Checkout must open in an in-app browser. If we
        // navigate the main WebView to Stripe, after payment Stripe redirects back to
        // the app origin (https://localhost) which cannot load -> blank white screen.
        const isNative = Capacitor.isNativePlatform();
        const origin = isNative ? BACKEND_URL : window.location.origin;
        const response = await createCheckoutSession(planKey, origin, billingCycle, currency);
        const checkoutUrl = response.data.checkout_url;

        if (isNative) {
          let settled = false;
          let poll = null;
          let listener = null;

          const checkActive = async () => {
            try {
              const sub = await getSubscription();
              return ['active', 'past_due', 'canceling'].includes(sub.data?.subscription_status);
            } catch (e) {
              return false;
            }
          };

          const finish = async (activated) => {
            if (settled) return;
            settled = true;
            if (poll) { clearInterval(poll); poll = null; }
            if (listener) { try { await listener.remove(); } catch (e) {} listener = null; }
            try { await Browser.close(); } catch (e) {}
            if (activated) {
              toast.success('Subscription active — welcome aboard!');
              navigate('/home');
            } else {
              setProcessingPlan(null);
            }
          };

          // Best-effort auto-detect: once Stripe's webhook flips the status to
          // active, close the in-app browser and continue automatically.
          poll = setInterval(async () => {
            if (await checkActive()) finish(true);
          }, 3000);
          // Safety net: stop polling after 3 minutes.
          setTimeout(() => { if (poll) { clearInterval(poll); poll = null; } }, 180000);

          // Fallback: user returns by closing the browser -> re-check once.
          listener = await Browser.addListener('browserFinished', async () => {
            finish(await checkActive());
          });

          await Browser.open({ url: checkoutUrl });
          return;
        }

        window.location.href = checkoutUrl;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
      setProcessingPlan(null);
    }
  };

  const applyPlanChange = async () => {
    if (!confirmChange) return;
    const planKey = confirmChange.planKey;
    setConfirmChange(null);
    setProcessingPlan(planKey);
    try {
      const res = await changePlan(planKey, billingCycle);
      toast.success(
        res.data?.immediate
          ? 'Plan updated — the change applies now.'
          : 'Plan updated — the new plan is now active.'
      );
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setProcessingPlan(null);
    }
  };

  const fmtMoney = (sym, amt) => `${sym}${Number(amt ?? 0).toFixed(2)}`;
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return ''; }
  };

  const getButtonText = (planKey) => {
    if (processingPlan === planKey) return 'Processing...';
    if (isPlanCurrent(planKey)) return 'Current Plan';
    // No paid subscription (trial running or expired) -> every plan is selectable.
    if (!hasPaidSubscription) return 'Choose Plan';

    // Switching billing cycle: monthly -> yearly is an upgrade (pay the full year);
    // yearly -> monthly is a downgrade.
    if (billingCycle !== currentBillingCycle) {
      return billingCycle === 'year' ? 'Upgrade' : 'Downgrade';
    }

    // Same billing cycle -> compare plan tiers.
    const planOrder = ['starter', 'growth', 'professional', 'business', 'enterprise'];
    const currentIndex = planOrder.indexOf(currentPlan);
    const targetIndex = planOrder.indexOf(planKey);
    
    if (targetIndex > currentIndex) return 'Upgrade';
    return 'Downgrade';
  };

  const getButtonStyle = (planKey) => {
    if (isPlanCurrent(planKey)) {
      return 'bg-slate-200 text-slate-500 cursor-not-allowed';
    }
    if (plans[planKey]?.recommended) {
      return 'bg-gradient-to-r from-blue-500 to-emerald-500 text-white hover:opacity-90';
    }
    return 'bg-slate-900 text-white hover:bg-slate-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  const planOrder = ['starter', 'growth', 'professional', 'business', 'enterprise'];

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container pb-28 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button 
            onClick={() => navigate('/settings')} 
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium mb-6"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back to Settings
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Choose Your Plan</h1>
          <p className="text-slate-600 font-medium text-lg max-w-2xl mx-auto">
            Choose the plan that fits your properties and your team. No contracts, cancel anytime.
          </p>
        </motion.div>

        {/* Billing cycle toggle — hidden on iOS (Apple = monthly only) */}
        {!isIOS() && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex flex-col items-center gap-3 mb-12"
        >
          <div className="relative inline-flex items-center p-1.5 bg-white rounded-2xl shadow-[0_8px_30px_rgba(15,23,42,0.08)] border border-slate-100" data-testid="billing-cycle-toggle">
            <button
              onClick={() => setBillingCycle('month')}
              data-testid="billing-monthly"
              className="relative z-10 px-8 h-11 rounded-xl text-sm font-semibold transition-colors duration-200"
            >
              {billingCycle === 'month' && (
                <motion.span
                  layoutId="billing-pill"
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 shadow-lg shadow-emerald-500/30"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
              <span className={`relative transition-colors ${billingCycle === 'month' ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
            </button>
            <button
              onClick={() => setBillingCycle('year')}
              data-testid="billing-yearly"
              className="relative z-10 px-8 h-11 rounded-xl text-sm font-semibold transition-colors duration-200"
            >
              {billingCycle === 'year' && (
                <motion.span
                  layoutId="billing-pill"
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-emerald-500 shadow-lg shadow-emerald-500/30"
                  transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                />
              )}
              <span className={`relative transition-colors ${billingCycle === 'year' ? 'text-white' : 'text-slate-500'}`}>Yearly</span>
            </button>
          </div>
          <AnimatePresence mode="wait">
            <motion.div
              key={billingCycle}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.2 }}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200"
              data-testid="billing-save-badge"
            >
              <Sparkles size={13} className="text-emerald-600" />
              <span className="text-xs font-bold text-emerald-700">
                {billingCycle === 'year' ? `You're saving ${discountPct}% with yearly billing` : `Switch to yearly and save ${discountPct}%`}
              </span>
            </motion.div>
          </AnimatePresence>
        </motion.div>
        )}

        {isIOS() && iosError && (
          <div
            className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-left"
            data-testid="ios-iap-error"
          >
            <p className="text-sm font-bold text-red-800 mb-1">Purchase error details</p>
            <p className="text-xs text-red-700 mb-2">Share this screenshot with support:</p>
            <pre className="text-[11px] leading-snug text-red-900 whitespace-pre-wrap break-words">{iosError}</pre>
          </div>
        )}

        {isIOS() && iosDiag && iosPackages.length === 0 && (
          <div
            className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left"
            data-testid="ios-iap-diagnostics"
          >
            <p className="text-sm font-bold text-amber-800 mb-1">Apple products not loaded yet</p>
            <p className="text-xs text-amber-700 mb-2">
              Diagnostic info (share this screenshot with support):
            </p>
            <pre className="text-[11px] leading-snug text-amber-900 whitespace-pre-wrap break-words">
{`configured: ${iosDiag.configured}
keyPresent: ${iosDiag.keyPresent}
currentOffering: ${iosDiag.currentOffering ?? 'none'}
currentPackages: ${iosDiag.currentPackages ?? 0}
allOfferings: ${(iosDiag.allOfferings || []).join(', ') || 'none'}
products: ${(iosDiag.productIds || []).join(', ') || 'none'}
error: ${iosDiag.error || 'none'}`}
            </pre>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {planOrder.map((planKey, index) => {
            const plan = plans[planKey];
            if (!plan) return null;
            
            const isCurrentPlan = isPlanCurrent(planKey);
            const isRecommended = plan.recommended;
            
            return (
              <motion.div
                key={planKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                className={`card-3d p-6 relative ${isRecommended ? 'ring-2 ring-blue-500' : ''}`}
                data-testid={`plan-card-${planKey}`}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Star size={12} fill="currentColor" />
                      RECOMMENDED
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-testid={`current-badge-${planKey}`}>
                      CURRENT
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  {billingCycle === 'year' ? (
                    <>
                      <div className="flex items-baseline justify-center gap-1" data-testid={`price-${planKey}`}>
                        <span className="text-4xl font-bold gradient-text">{sym}{plan.yearly_monthly_equivalent}</span>
                        <span className="text-slate-500">/month</span>
                      </div>
                      <div className="mt-1.5 flex items-center justify-center gap-2">
                        <span className="text-sm text-slate-400 line-through">{sym}{plan.price}/mo</span>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Save {discountPct}%</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5" data-testid={`yearly-total-${planKey}`}>
                        {sym}{plan.yearly_price} billed yearly
                      </p>
                      <p className="text-xs font-bold text-emerald-600 mt-1" data-testid={`yearly-save-${planKey}`}>
                        You save {sym}{(Math.round((plan.price * 12 - plan.yearly_price) * 100) / 100).toFixed(2)}/year
                      </p>
                    </>
                  ) : (
                    <div className="flex items-baseline justify-center gap-1" data-testid={`price-${planKey}`}>
                      {isIOS() && priceStringForPlan(iosPackages, planKey, 'month') ? (
                        <span className="text-4xl font-bold gradient-text">{priceStringForPlan(iosPackages, planKey, 'month')}</span>
                      ) : (
                        <span className="text-4xl font-bold gradient-text">{sym}{plan.price}</span>
                      )}
                      <span className="text-slate-500">/month</span>
                    </div>
                  )}
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                    <Home size={18} className="text-blue-500" />
                    <span className="text-sm font-medium text-slate-700">
                      Up to {plan.property_limit} properties
                    </span>
                  </div>
                </div>

                <div className="space-y-2.5 mb-6">
                  {plan.features.map((feature, idx) => {
                    const isCleaningCompany = /cleaning compan/i.test(feature);
                    const showNew = isCleaningCompany && planKey === 'growth';
                    return (
                      <div key={idx}>
                        <div className="flex items-center gap-2">
                          <Check size={16} className="text-emerald-500 flex-shrink-0" />
                          <span className="text-sm text-slate-600">{feature}</span>
                          {showNew && (
                            <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600" data-testid="cleaning-companies-new-badge">
                              NEW
                            </span>
                          )}
                        </div>
                        {showNew && (
                          <p className="text-xs text-slate-400 ml-6 mt-0.5" data-testid="cleaning-companies-desc">
                            Work with an external cleaning team on shared properties
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {isIOS() ? (
                  <button
                    onClick={() => handleBuyIOS(planKey)}
                    disabled={isCurrentPlan || processingPlan === planKey}
                    className={`btn-3d w-full h-12 rounded-xl font-semibold transition-all ${getButtonStyle(planKey)}`}
                    data-testid={`select-plan-${planKey}`}
                  >
                    {getButtonText(planKey)}
                  </button>
                ) : (
                  <button
                    onClick={() => handleSelectPlan(planKey)}
                    disabled={isCurrentPlan || processingPlan === planKey}
                    className={`btn-3d w-full h-12 rounded-xl font-semibold transition-all ${getButtonStyle(planKey)}`}
                    data-testid={`select-plan-${planKey}`}
                  >
                    {getButtonText(planKey)}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        {isIOS() && (
          <div className="mt-8 max-w-lg mx-auto text-center" data-testid="ios-subscription-footer">
            <button
              onClick={handleRestoreIOS}
              className="text-sm font-semibold text-slate-700 underline underline-offset-2"
              data-testid="restore-purchases-btn"
            >
              Restore Purchases
            </button>
            <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
              Your subscription renews automatically at the price shown
              for your selected plan. Payment is charged to your Apple ID. Subscriptions renew
              automatically unless cancelled at least 24 hours before the end of the current period.
              Manage or cancel anytime in your Apple ID settings.
            </p>
            <div className="mt-2 flex items-center justify-center gap-4 text-[11px] font-semibold text-slate-600">
              <button onClick={() => navigate('/terms')} className="underline" data-testid="terms-link">Terms of Use</button>
              <button onClick={() => navigate('/privacy')} className="underline" data-testid="privacy-link">Privacy Policy</button>
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="card-3d p-8 max-w-lg mx-auto">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Zap size={28} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Need More?</h3>
            <p className="text-slate-600 text-sm mb-6">
              Looking for custom solutions, higher limits, or dedicated support? Let's talk!
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="btn-3d h-12 px-8 rounded-xl font-semibold bg-gradient-to-r from-amber-400 to-orange-500 text-white hover:opacity-90"
            >
              Contact Us
            </button>
          </div>
        </motion.div>
      </main>

      <AlertDialog open={!!confirmChange} onOpenChange={(o) => { if (!o) setConfirmChange(null); }}>
        <AlertDialogContent data-testid="plan-change-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="plan-change-confirm-title">
              {confirmChange?.charges_today
                ? `Upgrade to ${confirmChange?.plan_name}`
                : `Switch to ${confirmChange?.plan_name}`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left text-slate-600">
                {confirmChange?.charges_today ? (
                  <>
                    <p data-testid="plan-change-charge-line">
                      You'll be charged{' '}
                      <strong className="text-slate-900">{fmtMoney(confirmChange?.currency_symbol, confirmChange?.amount_due_today)}</strong>{' '}
                      today — a prorated amount for the rest of your current billing period.
                    </p>
                    <p>
                      After that you'll pay{' '}
                      <strong className="text-slate-900">
                        {fmtMoney(confirmChange?.currency_symbol, confirmChange?.recurring_amount)}
                        {confirmChange?.interval === 'year' ? '/year' : '/month'}
                      </strong>
                      {confirmChange?.next_renewal_date ? `, starting ${fmtDate(confirmChange?.next_renewal_date)}.` : '.'}
                    </p>
                  </>
                ) : (
                  <>
                    <p data-testid="plan-change-charge-line">
                      <strong className="text-slate-900">No charge today.</strong> Your plan changes to {confirmChange?.plan_name} right away.
                    </p>
                    <p>
                      You'll be billed{' '}
                      <strong className="text-slate-900">
                        {fmtMoney(confirmChange?.currency_symbol, confirmChange?.recurring_amount)}
                        {confirmChange?.interval === 'year' ? '/year' : '/month'}
                      </strong>
                      {confirmChange?.next_renewal_date ? ` from your next renewal on ${fmtDate(confirmChange?.next_renewal_date)}.` : '.'}
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-change-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyPlanChange} data-testid="plan-change-confirm-button">
              {confirmChange?.charges_today
                ? `Confirm & pay ${fmtMoney(confirmChange?.currency_symbol, confirmChange?.amount_due_today)}`
                : 'Confirm change'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
